import { useEffect, useState } from 'react'
import { LoaderFunctionArgs, useLoaderData, useParams, useSearchParams } from 'react-router-dom'
import { useIsAccountClosed } from '../../components/AppShell'
import { Patient, fetchPatient } from '../../api/patients'
import malePatientImg from '../../assets/male_patient.png'
import femalePatientImg from '../../assets/female_patient.png'
import { ButtonAnchor, ButtonLink } from '../../components/common'
import styles from './PatientShowPage.module.scss'

function present(value: string | number | null | undefined): boolean {
  return value !== null && value !== undefined && String(value).length > 0
}

function capitalize(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1)
}

function titleize(value: string): string {
  return value
    .split(' ')
    .map((word) => (word.length > 0 ? capitalize(word) : word))
    .join(' ')
}

function InfoRow({ icon, label, value, first }: { icon: string; label: string; value: string; first?: boolean }) {
  return (
    <tr>
      <td style={first ? { width: '40%', borderTop: 0 } : undefined}>
        <i className={`fa ${icon}`} /> {label}
      </td>
      <td style={first ? { borderTop: 0 } : undefined}>{value}</td>
    </tr>
  )
}

export function PatientCompleteInfo({ patient, healthCenterId }: { patient: Patient; healthCenterId: number }) {
  const closed = useIsAccountClosed()
  const [tab, setTab] = useState<'basic_info' | 'health_info'>('basic_info')
  const [searchParams] = useSearchParams()
  const showInactive = searchParams.get('show_inactive') === 'true'

  const address = titleize(`${present(patient.domicile) ? capitalize(patient.domicile as string) : ''}${patient.commune ?? ''}`)

  const toggleInactiveParams = new URLSearchParams(searchParams)
  toggleInactiveParams.set('show_inactive', showInactive ? 'false' : 'true')

  return (
    <section className={`module module-no-heading ${styles.root}`}>
      <div className="module-inner">
        <div className="module-content collapse in">
          <div className="module-content-inner no-padding-bottom">
            <div className="profile">
              <div className="meta-wrapper" style={{ display: 'inline' }}>
                <div className="col-md-12 col-xs-12 text-center">
                  <img
                    src={patient.sex === 'F' ? femalePatientImg : malePatientImg}
                    className="profile-image img-responsive"
                    style={{ height: 110, width: 'auto', margin: 'auto' }}
                  />
                  <h5 className="text-center">
                    {present(patient.name) ? ` ${patient.name}` : ' Sin Información'}{' '}
                    {!closed && (
                      <ButtonLink
                        to={`/health_centers/${healthCenterId}/patients/${patient.id}/edit`}
                        variant="warning"
                        className="btn-fab btn-fab-mini btn-round"
                        style={{ margin: 0 }}
                      >
                        <i className="material-icons">edit</i>
                      </ButtonLink>
                    )}
                  </h5>
                  <div className="row">
                    <ul className="nav nav-tabs" style={{ margin: 0, padding: 0 }}>
                      <li className={tab === 'basic_info' ? 'active' : ''}>
                        <a
                          href="#basic_info"
                          onClick={(event) => {
                            event.preventDefault()
                            setTab('basic_info')
                          }}
                        >
                          Información Personal
                        </a>
                      </li>
                      <li className={tab === 'health_info' ? 'active' : ''}>
                        <a
                          href="#health_info"
                          onClick={(event) => {
                            event.preventDefault()
                            setTab('health_info')
                          }}
                        >
                          Información Contacto
                        </a>
                      </li>
                    </ul>
                    <div className="tab-content well">
                      {tab === 'basic_info' && (
                        <div id="basic_info" className="tab-pane active">
                          <table className="table table-responsive">
                            <tbody>
                              <InfoRow first icon="fa-id-card-o" label="Rut" value={present(patient.rut) ? (patient.rut as string) : 'Sin Información'} />
                              <InfoRow icon="fa-birthday-cake" label="Edad" value={patient.age === null ? 'Sin Información' : `${patient.age} años`} />
                              <InfoRow icon="fa-plus" label="Previsión" value={present(patient.prevision) ? capitalize(patient.prevision as string) : 'Sin Información'} />
                              <InfoRow icon="fa-heart" label="Estado Civil" value={present(patient.marital_status) ? capitalize(patient.marital_status as string) : 'Sin Información'} />
                              <InfoRow icon="fa-child" label="Nº Hijos" value={patient.number_of_childrens === null ? 'Sin Información' : String(patient.number_of_childrens)} />
                              <InfoRow icon="fa-university" label="Educación" value={present(patient.education) ? capitalize(patient.education as string) : 'Sin Información'} />
                              <InfoRow icon="fa-suitcase" label="Ocupación" value={present(patient.occupation) ? capitalize(patient.occupation as string) : 'Sin Información'} />
                            </tbody>
                          </table>
                        </div>
                      )}
                      {tab === 'health_info' && (
                        <div id="health_info" className="tab-pane active">
                          <table className="table table-responsive">
                            <tbody>
                              <InfoRow first icon="fa-phone" label="Personal" value={present(patient.phone_number) ? (patient.phone_number as string) : 'Sin Información'} />
                              <InfoRow icon="fa-envelope" label="Personal" value={present(patient.email) ? (patient.email as string) : 'Sin Información'} />
                              <InfoRow icon="fa-map-marker" label="Domicilio" value={address} />
                              <InfoRow icon="fa-user" label="Apoderado" value={present(patient.representative_name) ? titleize(patient.representative_name as string) : 'Sin Información'} />
                              <InfoRow icon="fa-phone" label="Apoderado" value={present(patient.representative_phone) ? (patient.representative_phone as string) : 'Sin Información'} />
                              <InfoRow icon="fa-phone" label="Emergencia" value={present(patient.emergency_number) ? (patient.emergency_number as string) : 'Sin Información'} />
                              <InfoRow icon="fa-user" label="Ente que deriva" value={present(patient.company_which_derives) ? titleize(patient.company_which_derives as string) : 'Sin Información'} />
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="row" style={{ marginBottom: 10 }}>
                    <a href={`${window.location.pathname}?${toggleInactiveParams.toString()}`}>
                      {showInactive ? 'Ver Ficha Clínica' : 'Ver Fichas Archivadas'}
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export function loader({ params }: LoaderFunctionArgs): Promise<Patient> {
  return fetchPatient(Number(params.healthCenterId), params.patientId as string)
}

export default function PatientShowPage() {
  const patient = useLoaderData() as Awaited<ReturnType<typeof loader>>
  const closed = useIsAccountClosed()
  const healthCenterId = Number(useParams().healthCenterId)

  return (
    <div>
      <div className="page-header">
        <h1>Paciente</h1>
      </div>
      <PatientCompleteInfo patient={patient} healthCenterId={healthCenterId} />
      <ButtonAnchor href={`/health_centers/${healthCenterId}/patients`} variant="default">
        Volver
      </ButtonAnchor>{' '}
      {!closed && (
        <ButtonLink to={`/health_centers/${healthCenterId}/patients/${patient.id}/edit`} variant="warning">
          Modificar
        </ButtonLink>
      )}
    </div>
  )
}
